import React, { useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { styled, keyframes } from "@mui/system";

// Keyframe animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(187, 134, 252, 0.7);
  }
  70% {
    transform: scale(1.02);
    box-shadow: 0 0 0 10px rgba(187, 134, 252, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(187, 134, 252, 0);
  }
`;

const glow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(187, 134, 252, 0.7),
                0 0 10px rgba(187, 134, 252, 0.5),
                0 0 15px rgba(187, 134, 252, 0.3);
  }
  50% {
    box-shadow: 0 0 10px rgba(187, 134, 252, 0.8),
                0 0 20px rgba(187, 134, 252, 0.6),
                0 0 30px rgba(187, 134, 252, 0.4);
  }
  100% {
    box-shadow: 0 0 5px rgba(187, 134, 252, 0.7),
                0 0 10px rgba(187, 134, 252, 0.5),
                0 0 15px rgba(187, 134, 252, 0.3);
  }
`;

const backgroundShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const borderPulse = keyframes`
  0% {
    border-color: #BB86FC;
  }
  50% {
    border-color: #03DAC6;
  }
  100% {
    border-color: #BB86FC;
  }
`;

// Styled components
const StyledLink = styled("a")(({ theme, ishovered }) => ({
  display: "inline-block",
  background:
    ishovered === "true"
      ? "linear-gradient(45deg, #BB86FC, #9b66d3, #03DAC6)"
      : "linear-gradient(45deg, #BB86FC, #9b66d3)",
  backgroundSize: ishovered === "true" ? "200% 200%" : "100% 100%",
  color: ishovered === "true" ? "#FFFFFF" : "#121212",
  fontWeight: 700,
  fontSize: "1.1rem",
  padding: "14px 38px",
  borderRadius: "4px", // Sharp corners
  textDecoration: "none",
  transition: "all 0.3s ease",
  animation: `${pulse} 2s infinite, ${glow} 3s infinite`,
  position: "relative",
  overflow: "hidden",
  border: "2px solid #BB86FC",
  animation: `${borderPulse} 4s infinite`,

  "&:hover": {
    animation: `${backgroundShift} 1.5s ease infinite, ${glow} 1.5s infinite`,
    transform: "translateY(-3px)",
    boxShadow: "0 7px 14px rgba(0, 0, 0, 0.3)",
  },

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(255, 255, 255, 0.2)",
    transform: "translateX(-100%) rotate(45deg)",
    transition: "transform 0.6s ease",
  },

  "&:hover::before": {
    transform: "translateX(100%) rotate(45deg)",
  },
}));

const CornerAccent = styled("div")(({ theme }) => ({
  position: "absolute",
  width: "12px",
  height: "12px",
  borderStyle: "solid",
  borderColor: "#BB86FC",
  borderWidth: "0 2px 2px 0",
  transition: "all 0.3s ease",
}));

const RegisterPrompt = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      mt={2}
      textAlign="center"
      sx={{
        position: "relative",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          borderRadius: "4px", // Sharp corners
          padding: 2,
          border: "1px solid rgba(255, 255, 255, 0.1)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Corner accents */}
        <CornerAccent sx={{ top: 0, right: 0, borderWidth: "2px 2px 0 0" }} />
        <CornerAccent sx={{ top: 0, left: 0, borderWidth: "2px 0 0 2px" }} />
        <CornerAccent
          sx={{ bottom: 0, right: 0, borderWidth: "0 2px 2px 0" }}
        />
        <CornerAccent sx={{ bottom: 0, left: 0, borderWidth: "0 0 2px 2px" }} />

        <Typography
          variant="body1"
          sx={{
            color: "rgba(255,255,255,0.9)",
            fontWeight: 500,
            mb: 2,
            fontSize: "1.0rem",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          Don't have an account?
        </Typography>

        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            transform: isHovered ? "translateY(-5px)" : "translateY(0)",
            transition: "transform 0.3s ease",
          }}
        >
          <StyledLink
            href="/create-user"
            ishovered={isHovered.toString()}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            Register Now
          </StyledLink>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "rgba(255,255,255,0.6)",
            mt: 2,
            fontStyle: "italic",
            letterSpacing: "0.5px",
          }}
        >
          Join us for exlusive features! ✨
        </Typography>
      </Paper>
    </Box>
  );
};

export default RegisterPrompt;
